import { mulberry32, seedFrom } from "./random.js";
/**
 * Maze generation and scoring.
 *
 * The maze is the first activity where the interesting measurement is not whether the child
 * succeeded. Almost every child eventually reaches the exit; what differs is HOW. Steps against
 * the shortest path, and how often they walk back the way they came, separate a child who looked
 * before moving from a child who tried every corridor — and those are not the same performance
 * even when both arrive.
 *
 * Both are recorded per trial. Neither is turned into a trait: planning is descriptive-only in
 * the Phase 0 triage, so this reports what a child did and never what they are.
 */
/** Sides of a cell, as a bitmask. A set bit means that side is open. */
export const UP = 1;
export const RIGHT = 2;
export const DOWN = 4;
export const LEFT = 8;
const OPPOSITE = { [UP]: DOWN, [DOWN]: UP, [LEFT]: RIGHT, [RIGHT]: LEFT };
/** Where a direction leads from a cell, or null at the edge of the grid. */
export function neighbour(cell, dir, size) {
    const r = Math.floor(cell / size);
    const c = cell % size;
    if (dir === UP)
        return r > 0 ? cell - size : null;
    if (dir === DOWN)
        return r < size - 1 ? cell + size : null;
    if (dir === LEFT)
        return c > 0 ? cell - 1 : null;
    if (dir === RIGHT)
        return c < size - 1 ? cell + 1 : null;
    return null;
}
/** Whether a step is legal: inside the grid, and not through a wall. */
export function canStep(maze, from, dir) {
    if (((maze.open[from] ?? 0) & dir) === 0)
        return null;
    return neighbour(from, dir, maze.size);
}
/**
 * Carves a perfect maze with an iterative depth-first search.
 *
 * "Perfect" means exactly one route between any two cells — no loops. That matters for what is
 * being measured: with loops, a child who wanders can stumble onto a shortcut, and the step count
 * stops saying anything about whether they planned. It also means every maze is solvable, so a
 * trial can never be impossible.
 *
 * Iterative rather than recursive because the recursion depth is the cell count, and a deep
 * recursion on a low-end phone is a stack overflow in the middle of a child's turn.
 */
function carve(size, rand) {
    const open = new Array(size * size).fill(0);
    const seen = new Array(size * size).fill(false);
    const stack = [0];
    seen[0] = true;
    while (stack.length > 0) {
        const cell = stack[stack.length - 1];
        const unvisited = [];
        for (const dir of [UP, RIGHT, DOWN, LEFT]) {
            const next = neighbour(cell, dir, size);
            if (next !== null && !seen[next])
                unvisited.push(dir);
        }
        if (unvisited.length === 0) {
            stack.pop();
            continue;
        }
        const dir = unvisited[Math.floor(rand() * unvisited.length)];
        const next = neighbour(cell, dir, size);
        // Both sides are opened: a wall is a property of the pair, and recording it on one side only
        // would let a child walk one way through it and not the other.
        open[cell] = (open[cell] ?? 0) | dir;
        open[next] = (open[next] ?? 0) | OPPOSITE[dir];
        seen[next] = true;
        stack.push(next);
    }
    return open;
}
/** Shortest-path distances from a cell, by breadth-first search. */
function distances(open, size, from) {
    const dist = new Array(size * size).fill(-1);
    dist[from] = 0;
    const queue = [from];
    for (let head = 0; head < queue.length; head++) {
        const cell = queue[head];
        for (const dir of [UP, RIGHT, DOWN, LEFT]) {
            if (((open[cell] ?? 0) & dir) === 0)
                continue;
            const next = neighbour(cell, dir, size);
            if (next === null || (dist[next] ?? -1) >= 0)
                continue;
            dist[next] = dist[cell] + 1;
            queue.push(next);
        }
    }
    return dist;
}
/**
 * A maze for one trial.
 *
 * `difficulty` is the number of cells per side. It stays small — a 7×7 on a 360 px phone gives
 * ~45 px cells, well under the ~76 px a child needs to hit reliably, so the size is bounded by
 * what a hand can do rather than by what fits.
 *
 * The goal is placed at the FARTHEST cell from the start rather than at a corner. A corner goal is
 * often a short hop away in a carved maze, which would make a hard-looking maze trivial and the
 * step count meaningless.
 */
/**
 * Cells per side.
 *
 * The upper bound is legibility, not touch size: movement is a D-pad of four fixed arrows, so a
 * maze cell only has to be VISIBLE. An 8×8 on a 360 px phone gives ~40 px cells with walls, which
 * still reads as a maze; past that the corridors close up visually.
 */
export const MAZE_MIN_SIZE = 3;
export const MAZE_MAX_SIZE = 8;
export function generateMaze(task, formId, trialIndex, difficulty) {
    if (difficulty < MAZE_MIN_SIZE || difficulty > MAZE_MAX_SIZE) {
        throw new Error(`maze size ${difficulty} out of range for ${task.id}`);
    }
    const size = difficulty;
    const rand = mulberry32(seedFrom(formId, trialIndex, difficulty));
    const open = carve(size, rand);
    // Start somewhere on the edge, so the maze reads as having an entrance.
    const edges = [];
    for (let i = 0; i < size * size; i++) {
        const r = Math.floor(i / size);
        const c = i % size;
        if (r === 0 || c === 0 || r === size - 1 || c === size - 1)
            edges.push(i);
    }
    const start = edges[Math.floor(rand() * edges.length)];
    const dist = distances(open, size, start);
    let goal = start;
    for (let i = 0; i < dist.length; i++) {
        if (dist[i] > dist[goal])
            goal = i;
    }
    return { size, open, start, goal, optimalSteps: dist[goal] };
}
/**
 * How many steps a child may take before the trial ends.
 *
 * Generous — twice the shortest route plus a few — because the point is not to punish exploring.
 * A budget is needed at all so that difficulty can adapt: without one every trial ends in success
 * eventually, the step-up rule fires forever, and a child is pushed to a maze they cannot hold in
 * their head while the record says they were doing fine.
 */
export function stepBudget(maze) {
    return maze.optimalSteps * 2 + 4;
}
/** Reaching the exit inside the budget. Efficiency is recorded separately, never scored here. */
export function scoreMazeTrial(maze, reachedGoal, stepsTaken) {
    return reachedGoal && stepsTaken <= stepBudget(maze);
}
//# sourceMappingURL=maze.js.map